import React, { useContext, useEffect, useRef, useState } from "react";
import { generateUrl } from "../UrlProcessor/GenerateUrl.tsx";
import { SettingsContext } from "./SettingsContext.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * Copies a link that reopens the generator with the current settings.
 *
 * This used to be a button labelled "Generate URL" that wrote to the clipboard
 * and revealed a hidden span, with no explanation of what the link was for and
 * no way to tell whether the copy had worked. It is the most shareable thing on
 * the site - a teacher can hand a colleague the exact worksheet - so it says
 * what it does, and it tells the truth about whether it succeeded.
 *
 * `path` is the page the link opens on - the landing page this sheet was made
 * on - so the colleague lands where the teacher was, not on the home page.
 *
 * Since ADR 0061 it sits at the right-hand end of the bar above the preview, as
 * a ghost button. The hint that says what the link is for is no longer printed
 * beside it - the bar has no room for a paragraph - but it is still the
 * button's description, for a screen reader and as a tooltip.
 */
export function ShareWorksheet({ path }: { path?: string } = {}) {
    const { settings, seed } = useContext(SettingsContext);
    const [state, setState] = useState<"idle" | "copied" | "manual">("idle");
    const [url, setUrl] = useState("");
    const clearTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const manualInput = useRef<HTMLInputElement>(null);
    const t = useTranslation();

    useEffect(() => () => clearTimeout(clearTimer.current), []);

    // Any settings change makes a previously copied link stale, so the
    // confirmation goes away rather than describing a worksheet that has moved on.
    useEffect(() => {
        setState("idle");
        clearTimeout(clearTimer.current);
    }, [settings]);

    async function copyLink() {
        const link = generateUrl(settings, seed, path);
        setUrl(link);

        try {
            // Undefined outside a secure context, and can reject when the
            // document is not focused or permission is refused.
            await navigator.clipboard.writeText(link);
            setState("copied");
            clearTimeout(clearTimer.current);
            clearTimer.current = setTimeout(() => setState("idle"), 6000);
        } catch {
            // Rather than claim a copy that did not happen, show the link.
            setState("manual");
        }
    }

    useEffect(() => {
        if (state === "manual") {
            manualInput.current?.focus();
            manualInput.current?.select();
        }
    }, [state]);

    return (
        <div className="shareWorksheet">
            <button
                type="button"
                className="btn btn-ghost generateUrl"
                title={t('settings.shareHint')}
                aria-describedby="shareHint"
                onClick={copyLink}
            >
                {t('settings.copyLinkButton')}
            </button>
            <span id="shareHint" className="srOnly">
                {t('settings.shareHint')}
            </span>

            {/* One live region rather than one per outcome, so a screen reader is
                told what happened instead of what element appeared. */}
            <div role="status" aria-live="polite">
                {state === "copied" && <span className="clipboardMessage">{t('settings.linkCopied')}</span>}
                {state === "manual" && (
                    <span className="clipboardMessage shareManual">
                        {t('settings.shareManualMessage')}
                    </span>
                )}
            </div>

            {state === "manual" && (
                <input
                    ref={manualInput}
                    className="shareManualInput"
                    readOnly
                    value={url}
                    aria-label={t('settings.linkToWorksheetAria')}
                    onFocus={(event) => event.target.select()}
                />
            )}
        </div>
    );
}

export default ShareWorksheet;
